// Error tracking (plan Lot A.3). No-op when VITE_SENTRY_DSN is unset (local
// dev, and any build that doesn't set it) — never point local dev at the
// real DSN, it would burn the project's error quota on exceptions you're
// already seeing in the console.
//
// Copied from memoneo's frontend/src/lib/sentry.ts (2026-09-24) — same
// queue-until-init / no-op-without-DSN contract, verbatim. Only the
// environment detection and the IGNORED_ERRORS list differ, see below.
//
// @sentry/react is loaded via dynamic import() rather than a static import,
// so it becomes its own async chunk instead of bundling into the entry chunk
// (it's ~40 KB min+gzip, all dead weight for the ~90%+ of loads where nothing
// ever errors). initSentry() kicks the import off fire-and-forget — Sentry
// coming online a few hundred ms late is an acceptable tradeoff. captureException()/
// setUser() below are the two calls other modules need before the module has
// necessarily landed, so they queue until it does (or no-op forever if
// there's no DSN, matching the previous behavior of an inert Sentry.init()-
// less SDK).
import type * as SentryReact from "@sentry/react";

// Known-noisy errors, kept even though this app (unlike memoneo) has no
// dedicated handler for any of them — the underlying failure modes are just
// as real here (a PWA with hashed chunks, a cross-origin API call through
// Cloudflare, browser extensions mutating the DOM), so sending them to
// Sentry unfiltered would still drown real issues under repeat noise from
// day one. If a dedicated handler is ever added here (a reload-on-stale-
// chunk helper, a named "api_call_failed" message, …), replace the matching
// entry below with that signal instead of just filtering it — see memoneo's
// own sentry.ts for the pattern.
const IGNORED_ERRORS = [
  // Generic exception the browser throws for a stale-PWA chunk 404 after a
  // deploy — every hashed asset reference in an already-open tab can 404
  // once a new build has shipped and old chunks are gone from the CDN.
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  // Google Translate (or a similar extension) mutating the DOM under
  // React's feet — a browser-extension problem, not ours.
  /NotFoundError.*removeChild/,
  // Cloudflare's 100s timeout on a slow API call surfaces as a plain fetch
  // failure client-side, with no CORS/status detail — not a front-end bug.
  "Failed to fetch",
  "Load failed",
  // Standard noise from other extensions / the browser itself.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];

const IGNORED_URLS = [/^chrome-extension:\/\//, /^moz-extension:\/\//];

let sentry: typeof SentryReact | null = null;
// Only queue calls made before the module lands when we actually know it's
// going to land (a DSN is configured) — otherwise this would grow forever in
// any DSN-less environment (local dev, PR previews) since `sentry` would
// never flush the queue.
let dsnConfigured = false;
// A single ordered queue for both call kinds, rather than two parallel
// arrays — captureException()/captureMessage() calls made before init lands
// can be interleaved by other code, and replaying them out of relative order
// would be a surprising (if minor) behavior change from calling straight
// into the SDK.
type PendingCall =
  | { kind: "exception"; error: unknown }
  | { kind: "message"; message: string; context?: CaptureMessageContext }
  | { kind: "breadcrumb"; message: string; context?: BreadcrumbContext };
let pendingCalls: PendingCall[] = [];
let pendingUser: { called: boolean; value: SentryReact.User | null } = { called: false, value: null };
// Reference to the in-flight dynamic import, so flush() (below) can await
// "the module either lands or definitively fails" without polling — it's
// the same promise initSentry() itself chains off of.
let sentryImportPromise: Promise<typeof SentryReact> | null = null;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  dsnConfigured = true;
  const importPromise = import("@sentry/react");
  sentryImportPromise = importPromise;
  void importPromise
    .then((Sentry) => {
      Sentry.init({
        dsn,
        // `import.meta.env.MODE` is "production" for `npm run build` and
        // "staging" for `npm run build:staging` (see vite.config.ts /
        // .env.production / .env.staging) — this repo has no IS_TEST_ENV
        // equivalent (single build target per mode, unlike memoneo's one
        // build pointed at either origin by env vars), so the build mode
        // itself is the environment.
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_RELEASE || undefined,
        tracesSampleRate: 0, // error tracking only for the beta — no tracing
        sendDefaultPii: false,
        ignoreErrors: IGNORED_ERRORS,
        denyUrls: IGNORED_URLS,
      });
      sentry = Sentry;
      if (pendingUser.called) Sentry.setUser(pendingUser.value);
      for (const call of pendingCalls) {
        if (call.kind === "exception") Sentry.captureException(call.error);
        else if (call.kind === "message") Sentry.captureMessage(call.message, call.context);
        else {
          Sentry.addBreadcrumb({
            message: call.message, category: call.context?.category, level: call.context?.level, data: call.context?.data,
          });
        }
      }
      pendingCalls = [];
    })
    .catch(() => {
      // The @sentry/react chunk is itself just another hashed chunk, so it
      // can 404 under the exact same stale-PWA-precache conditions the
      // first two IGNORED_ERRORS entries above cover — in which case there
      // is no error reporter to hand anything to. Swallow it here (rather
      // than an unhandled rejection) and leave pendingCalls queued forever;
      // flush() below is what bounds how long a caller will wait on that.
    });
}

/** Wrapper for `Sentry.captureException` — safe to call before initSentry()'s
 *  dynamic import has resolved (queues) or when there's no DSN (no-ops). */
export function captureException(error: unknown): void {
  if (sentry) {
    sentry.captureException(error);
  } else if (dsnConfigured) {
    pendingCalls.push({ kind: "exception", error });
  }
}

/** Context accepted by captureMessage() — a subset of Sentry's CaptureContext
 *  covering what callers in this repo actually need (severity + structured
 *  tags/extra), rather than exposing the SDK's full scope-mutation surface. */
export interface CaptureMessageContext {
  level?: SentryReact.SeverityLevel;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

/** Wrapper for `Sentry.captureMessage` — same queue-or-no-op contract as
 *  captureException() above (safe before init lands, no-ops without a DSN). */
export function captureMessage(message: string, context?: CaptureMessageContext): void {
  if (sentry) {
    sentry.captureMessage(message, context);
  } else if (dsnConfigured) {
    pendingCalls.push({ kind: "message", message, context });
  }
}

/** Context accepted by addBreadcrumb() — same reasoning as
 *  CaptureMessageContext above. */
export interface BreadcrumbContext {
  category?: string;
  level?: SentryReact.SeverityLevel;
  data?: Record<string, unknown>;
}

/** Wrapper for `Sentry.addBreadcrumb` — same queue-or-no-op contract as
 *  captureException() above (safe before init lands, no-ops without a
 *  DSN). Breadcrumbs are trail-of-events context attached to whatever
 *  error or message is captured next, not events of their own — used here
 *  (ChapterSceneFullscreen.tsx, chapterSceneFullscreenDiagnostics.ts) to
 *  build a timeline around a bug report that isn't reproducible locally:
 *  the iPhone-only "pinching back out freezes the app and stops audio, as
 *  if the page reloaded" report on the chapter illustration's full-screen
 *  view. */
export function addBreadcrumb(message: string, context?: BreadcrumbContext): void {
  if (sentry) {
    sentry.addBreadcrumb({
      message, category: context?.category, level: context?.level, data: context?.data,
    });
  } else if (dsnConfigured) {
    pendingCalls.push({ kind: "breadcrumb", message, context });
  }
}

/** Wrapper for `Sentry.setUser` — same queue-or-no-op contract as
 *  captureException() above. */
export function setUser(user: SentryReact.User | null): void {
  if (sentry) {
    sentry.setUser(user);
  } else if (dsnConfigured) {
    pendingUser = { called: true, value: user };
  }
}

/**
 * Flush any events already handed to the SDK's transport, bounded by
 * `timeoutMs` no matter what. captureException()/captureMessage() only
 * *enqueue* an event — they return before the network request that ships it
 * exists — so a caller about to do something that can kill in-flight
 * requests (window.location.reload(), most notably) needs to await this
 * first, or the event is silently lost.
 *
 * Never throws and always settles within timeoutMs, including when:
 *  - there's no DSN (nothing was ever going to be sent — resolves immediately),
 *  - the @sentry/react dynamic import is still in flight (awaits
 *    `sentryImportPromise` instead of polling, but only up to the timeout),
 *  - that import itself rejects — the chunk can 404 in EXACTLY the
 *    stale-deploy scenario this is used for (see initSentry()'s `.catch()`
 *    above) — in which case there is nothing to flush and we resolve rather
 *    than hang waiting for a module that will never arrive,
 *  - `Sentry.flush()` itself throws or never settles.
 */
export function flush(timeoutMs: number): Promise<void> {
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  const attempt = (async () => {
    try {
      const Sentry = sentry ?? (dsnConfigured && sentryImportPromise ? await sentryImportPromise : null);
      if (Sentry) await Sentry.flush(timeoutMs);
    } catch {
      /* best-effort — a failed flush must never block the caller */
    }
  })();
  return Promise.race([attempt, timeout]);
}
